import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';
import SEO, { SITE_URL } from '../../components/feature/SEO';

const values = [
  {
    icon: 'ri-hammer-line',
    title: 'Craftsmanship',
    titleKo: '장인정신',
    desc: '35년 이상의 경험으로 쌓인 장인 기술.&nbsp; 최고의 정성을 담습니다.',
  },
  {
    icon: 'ri-star-line',
    title: 'Excellence',
    titleKo: '탁월함',
    desc: '타협 없는 품질 기준. 전 세계 럭셔리 브랜드가 신뢰하는 완성도.',
  },
  {
    icon: 'ri-heart-line',
    title: 'Passion',
    titleKo: '열정',
    desc: '자수에 대한 순수한 사랑에서 비롯된 창의성. 매 작품이 예술입니다.',
  },
];

const milestones = [
  { year: '1991', event: '하이반텍스 창립', desc: '전통 자수 기법으로 시작한 첫 걸음' },
  { year: '1995', event: '불리온 전문화', desc: '불리온 엠블럼 전문 제조 라인 구축' },
  { year: '2005', event: '글로벌 확장', desc: '해외 럭셔리 브랜드와의 협업 시작' },
  { year: '2015', event: '프리미엄 인증', desc: '국제 공인 품질 인증 취득' },
  { year: '2024', event: '혁신과 전통', desc: '전통 기법과 현대 기술의 조화' },
];

const aboutJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '하이반텍스 소개 | About HivanTex',
    url: `${SITE_URL}/about`,
    description: '1991년 설립된 하이반텍스(HivanTex)의 35년 장인정신과 브랜드 스토리를 소개합니다.',
    inLanguage: 'ko',
    mainEntity: {
      '@type': 'Organization',
      name: '하이반텍스 HivanTex',
      foundingDate: '1991',
      foundingLocation: '서울특별시, 대한민국',
      numberOfEmployees: { '@type': 'QuantitativeValue', value: '전문 장인 팀' },
      description: '프리미엄 불리온 엠블럼 및 핸드메이드 자수 전문 기업',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '답십리로68길 31, 3층',
        addressLocality: '동대문구',
        addressRegion: '서울특별시',
        addressCountry: 'KR',
      },
    },
  },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen font-sans bg-cream">
      <SEO jsonLd={aboutJsonLd} />
      <Navbar />

      {/* Hero */}
      <section className="relative bg-[#181818] pt-40 pb-28 px-16 overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              'radial-gradient(ellipse 50% 60% at 30% 60%, rgba(201,169,97,0.06) 0%, transparent 60%)',
          }}
        />
        <div className="absolute top-28 left-14 w-12 h-12 border-l border-t border-gold/25" />
        <div className="absolute top-28 right-14 w-12 h-12 border-r border-t border-gold/25" />
        <div className="max-w-[1300px] mx-auto relative z-10">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-8 h-px bg-gold/50" />
            <span className="text-gold text-[10px] tracking-[0.3em] uppercase font-sans">About Us</span>
          </div>
          <h1 className="font-serif text-cream text-6xl font-light leading-tight tracking-wide mb-6">
            하이반텍스의&nbsp;<em className="not-italic text-gold">이야기</em>
          </h1>
          <p className="text-cream/40 text-sm font-sans font-light leading-8 max-w-xl">
            장인정신과 예술이 만나는 곳, 35년의 역사와 열정으로 만들어온 우리만의 이야기를 전합니다.
          </p>
        </div>
      </section>

      {/* Story section */}
      <section className="py-28 px-16">
        <div className="max-w-[1300px] mx-auto grid grid-cols-2 gap-24 items-center">
          <div>
            <p className="text-gold text-[10px] tracking-[0.3em] uppercase font-sans mb-6">Our Story</p>
            <h2 className="font-serif text-charcoal text-4xl font-light leading-snug mb-8 tracking-wide">
              1991년 설립된 핸드메이드 엠블럼 및 자수 전문 기업입니다.
            </h2>
            <p className="text-charcoal/55 text-sm leading-8 font-sans font-light mb-6">
              The Art of Heritage: 한 땀의 예술로 완성하는 가치<br />
              1991년 시작된 하이반텍스(Hivantex)는 지난 35년간 단순한 자수를 넘어, 브랜드의 정체성을 상징하는 &apos;작품&apos;을 빚어왔습니다.<br />
              하이반텍스의 엠블럼은 차갑습니다. 기계 작업으로는 흉내 낼 수 없는 핸드메이드만의 입체감과 생동감을 담고 있습니다. 금사와 은사가 빛을 머금으며 만들어내는 섬세한 결, 수만 번의 손길을 거쳐 완성되는 고품격 와펜은 의류의 품격을 결정짓는 마지막 한 조각이 됩니다.<br />
              국내 패션 브랜드 BCBG의 탄생부터 지금까지, 최고의 파트너로서 신뢰를 쌓아온 하이반텍스. 우리는 당신의 브랜드가 가진 철학을 가장 고귀한 형태로 시각화하는 &apos;엠블럼 마스터&apos;입니다.
            </p>
            <p className="text-charcoal/55 text-sm leading-8 font-sans font-light mb-10">
              불리온 엠블럼에서 섬세한 핸드메이드 자수까지, 우리의 모든 제품은 고객의
              브랜드 아이덴티티를 완성하는&nbsp; 예술 작품입니다. 전 세계 럭셔리 브랜드가 하이반텍스를 신뢰하는 이유가 바로 여기에 있습니다.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 border border-gold/50 text-gold text-[10px] tracking-[0.2em] uppercase px-10 py-3.5 hover:bg-gold hover:text-charcoal transition-all duration-300 font-sans whitespace-nowrap cursor-pointer"
            >
              상담 신청하기
              <i className="ri-arrow-right-line text-xs" />
            </Link>
          </div>

          {/* Stat boxes */}
          <div className="grid grid-cols-2 gap-4">
            {[
              { n: '35+', l: '년 경력' },
              { n: '500+', l: '글로벌 클라이언트' },
              { n: '10K+', l: '완성 작품' },
              { n: '100%', l: '핸드메이드' },
            ].map((s) => (
              <div key={s.n} className="bg-[#1C1C1C] p-8 flex flex-col items-center justify-center aspect-square">
                <div className="w-8 h-px bg-gold/40 mb-5" />
                <span className="font-serif text-gold text-4xl font-light block mb-2">{s.n}</span>
                <span className="text-cream/30 text-[10px] tracking-[0.15em] uppercase font-sans text-center">{s.l}</span>
                <div className="w-8 h-px bg-gold/40 mt-5" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="bg-[#1C1C1C] py-24 px-16">
        <div className="max-w-[1300px] mx-auto">
          <div className="text-center mb-16">
            <p className="text-gold text-[10px] tracking-[0.3em] uppercase font-sans mb-4">Our Values</p>
            <h2 className="font-serif text-cream text-4xl font-light tracking-wide">핵심 가치</h2>
          </div>
          <div className="grid grid-cols-3 gap-8">
            {values.map((v) => (
              <div key={v.title} className="text-center py-10 px-8 border border-cream/5 hover:border-gold/25 transition-colors duration-300">
                <div className="w-12 h-12 flex items-center justify-center border border-gold/30 mx-auto mb-6">
                  <i className={`${v.icon} text-gold text-lg`} />
                </div>
                <h3 className="font-serif text-cream text-xl font-light mb-1 tracking-wide">{v.title}</h3>
                <p className="text-gold text-[10px] tracking-[0.2em] uppercase font-sans mb-5">{v.titleKo}</p>
                <p className="text-cream/40 text-xs leading-7 font-sans font-light">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-28 px-16">
        <div className="max-w-[900px] mx-auto">
          <div className="text-center mb-16">
            <p className="text-gold text-[10px] tracking-[0.3em] uppercase font-sans mb-4">History</p>
            <h2 className="font-serif text-charcoal text-4xl font-light tracking-wide">브랜드 연혁</h2>
          </div>
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-px bg-gold/20 -translate-x-1/2" />
            <div className="space-y-12">
              {milestones.map((m, i) => (
                <div key={m.year} className={`flex items-center gap-10 ${i % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}>
                  <div className={`flex-1 ${i % 2 === 0 ? 'text-right' : 'text-left'}`}>
                    <p className="font-serif text-gold text-2xl font-light mb-1">{m.year}</p>
                    <h4 className="text-charcoal text-sm font-sans font-medium tracking-wide mb-1">{m.event}</h4>
                    <p className="text-charcoal/45 text-xs font-sans font-light">{m.desc}</p>
                  </div>
                  <div className="w-3 h-3 rounded-full border-2 border-gold bg-cream shrink-0 z-10" />
                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
